import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { verifyEmail } from '@/api/auth'
import { ApiError } from '@/api/client'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

type Status = 'verifying' | 'success' | 'invalid' | 'expired' | 'failed' | 'missingToken'

// API のエラーコード → 表示する状態
function statusFromError(err: unknown): Status {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string } } | null | undefined
    const code = body?.error?.code
    if (code === 'verification_token_expired') return 'expired'
    if (code === 'invalid_verification_token') return 'invalid'
  }
  return 'failed'
}

// 確認メールのリンク先(/verify-email?token=...)。ログインしていない端末で開かれてもよい。
export function VerifyEmailPage() {
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'missingToken')
  // トークンは一度使うと無効になるので、StrictMode の二重実行でも1回だけ送る
  const requested = useRef(false)

  useEffect(() => {
    if (!token || requested.current) return
    requested.current = true
    verifyEmail(token)
      .then(() => {
        setStatus('success')
        // ログイン中ならバナーを消すためにユーザー情報を取り直す
        refreshUser().catch(() => undefined)
      })
      .catch((err: unknown) => setStatus(statusFromError(err)))
  }, [token, refreshUser])

  const ok = status === 'success'

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">{t('verifyEmail.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'verifying' ? (
            <p className="text-sm text-muted-foreground">{t('verifyEmail.verifying')}</p>
          ) : (
            <div
              className={
                ok
                  ? 'flex items-start gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800'
                  : 'flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'
              }
            >
              {ok ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              )}
              <span>{t(`verifyEmail.${status}`)}</span>
            </div>
          )}
        </CardContent>
        {status !== 'verifying' && (
          <CardFooter>
            <Button asChild className="w-full">
              <Link to={user ? '/dashboard' : '/login'}>
                {user ? t('verifyEmail.toDashboard') : t('verifyEmail.toLogin')}
              </Link>
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  )
}
